import has from 'lodash/has';
import React, { useEffect, useRef, useState } from 'react';
import { isMobileOnly as isMobile } from 'react-device-detect';
import { useInView } from 'react-intersection-observer';
import { connect } from 'react-redux';
import { BQLink } from '../../atoms/bqlink';
import DfpAd from '../../atoms/dfp-ad';
import { makeApiCall, isWithinTradingHours, widgetLink } from '../../../helpers/utils';
import { ArrowRightIcon } from '../../atoms/material-icons';
import { useInterval } from '../../../helpers/custom-hooks';
import './budget-impacts.m.css';


const BudgetImpacts = ({ features, widget = false }) => {
  const [sectionTitle, setSectionTitle] = useState('BUDGET IMPACT');
  const [firstWord, ...restWords] = sectionTitle?.split(' ');
  const restText = restWords.join(' ');
  const [summary, setSummary] = useState('Glimpse of the Sectors & Stocks impacted');
  const [currentTab, setCurrentTab] = useState('');
  const [currentSector, setCurrentSector] = useState(null);
  const [cardsData, setCardsData] = useState([]);
  const [showMore, setShowMore] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [rightValue, setRightValue] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [tabs, setTabs] = useState([]);
  const [widgetParam, setWidgetParam] = useState('');
  const [hasStory, setHasStory] = useState(false);
  const [ref, inView] = useInView();
  const menuRef = useRef(null);

  let maxPositivePercentage = 0;
  let maxNegativePercentage = 0;
  let negativeIndex = 0;
  let bgColor = '#FFFFFF';
  let greenColor = 0;
  let redColor = 0;

  useEffect(() => {
    try {
      makeApiCall(features?.heatmap?.sector_url).then((res) => {
        if (res && res?.length > 0) {
          if (!!res?.[0]?.Widgetlink) {
            setSectionTitle(res?.[0]?.Widgetlink);
          }
          if (!!res?.[0]?.embed) {
            setSummary(res?.[0]?.embed);
          }
          setCurrentTab(res?.[0]?.EmbedCode.replace(/\s/g, '_').replace(/&/g, '_'));
          const data = [];
          res.forEach((item) => {
            let slug = '';
            if (!!item?.Link) {
              const url = new URL(item?.Link);
              slug = url.pathname;
            }
            data.push({
              name: item?.EmbedCode,
              key: item?.EmbedCode.replace(/\s/g, '_').replace(/&/g, '_'),
              hero_image: item?.FullImage || '',
              headline: item?.Title || '',
              slug,
              sectorId: item?.author,
            });
          });
          setTabs(data);
        }
      });
    } catch (e) {
      console.error('Error in fetching budget impacts tabs', e);
    }
  }, []);
  useEffect(() => {
    if (initialLoading && tabs?.length > 0) {
      setInitialLoading(false);
      setHasStory(!!tabs[0]?.hero_image);
      getSectorData(tabs[0].sectorId, initialLoading);
      setCurrentSector(tabs[0].sectorId);
    }
  }, [tabs]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const view = urlParams.get('view');
    if (widget && view === 'apps') {
      setWidgetParam('?view=apps&openexternal=1');
    }
  }, []);

  useInterval(() => {
    getSectorData(currentSector);
  }, 60000);

  const getSectorData = (id, initialLoading = false, tabSwitch = false) => {
    if (
      (isWithinTradingHours(features?.trading_holidays, features?.special_trading_days) &&
        inView) ||
      initialLoading ||
      tabSwitch
    ) {
      makeApiCall(`/foapi/market/getSectorStocks?sectorId=${id}`).then((res) => {
        if (res.responseCode === 200) {
          setCardsData(res.data);
        }
      });
    }
  };

  const selectTab = (e, tab) => {
    e.target.offsetParent.children[0].scrollLeft =
      e.target.offsetLeft - e.target.offsetParent.children[0].offsetWidth / (isMobile ? 4 : 2);
    setScrollPosition(
      e.target.offsetLeft - e.target.offsetParent.children[0].offsetWidth / (isMobile ? 4 : 2),
    );
    setRightValue(
      e.target.offsetLeft - e.target.offsetParent.children[0].offsetWidth / (isMobile ? 4 : 2) >=
        e.target.offsetParent.children[0].scrollWidth -
          e.target.offsetParent.children[0].offsetWidth,
    );
    setCurrentTab(tab.key);
    if (currentSector !== tab.sectorId) {
      setCardsData([]);
      setHasStory(!!tabs?.find(t => t.key === tab.key)?.hero_image);
      getSectorData(tab.sectorId, false, true);
      setCurrentSector(tab.sectorId);
      setShowMore(false);
    }
  };

  const renderCards = () => {
    const sortedData = cardsData.slice().sort((a, b) => b.changePer - a.changePer);
    return sortedData
      .slice(0, hasStory ? isMobile ? 6 : 10 : isMobile ? 9 : 15)
      .map((card, index) => {
        if (maxPositivePercentage === 0) {
          maxPositivePercentage = card.changePer;
          greenColor = (80 / maxPositivePercentage) * card.changePer;
        } else {
          greenColor = 80 + 10 * (index + 1);
        }

        if (maxNegativePercentage === 0 && card.changePer < 0) {
          maxNegativePercentage = card.changePer;
          redColor = 235;
          negativeIndex = 2;
        } else {
          redColor = 235 - (235 / sortedData.length) * negativeIndex;
          negativeIndex++;
        }

        if (card.changePer > 0) {
          bgColor = `rgb(0,${greenColor},0)`;
        } else if (card.changePer < 0) {
          redColor = redColor === 0 ? 100 : redColor;
          bgColor = `rgb(${redColor},0,0)`;
        } else if (card.changePer === 0) {
          bgColor = `#3A3A3A`;
        }

        return (
          <div
            styleName={`card ${cardsData.length < 20 ? 'less-cards' : ''}`}
            key={card.name.replace(/\s/g, '_') + '_' + index}
            role="button"
            style={{
              background: `${bgColor} 0 0 no-repeat padding-box`,
            }}
            onClick={() => takeToStockPage(card?.exchangeSymbol, card?.entityId)}
            onTouchStart={() => takeToStockPage(card?.exchangeSymbol, card?.entityId)}
          >
            <div styleName="stock-name" title={card.name}>
              {card.name}
            </div>
            <div styleName="price-ctx">
              <span styleName="current-price">{card.lastPrice}</span>
              <div styleName="change-ctx">
                {card.changePer !== 0 && (
                  <span styleName={`change-symbol ${card.changePer < 0 ? 'negative-change' : ''}`}>
                    {card.changePer < 0 ? '▼' : '▲'}
                  </span>
                )}
                <span styleName={`change-perc ${card.changePer === 0 ? 'no-change' : ''}`}>
                  {card.changePer}%
                </span>
              </div>
            </div>
          </div>
        );
      });
  };

  const handleNextClick = () => {
    const change = isMobile ? 200 : 225;
    const menu = menuRef.current;
    const scrollWidth = menu.scrollWidth - menu.clientWidth;
    menu.scrollLeft +=
      scrollPosition % change !== 0 && !isMobile ? change + (scrollPosition % change) : change;
    if (scrollPosition < scrollWidth) {
      const newPosition =
        scrollPosition +
        (scrollPosition % change !== 0 && !isMobile ? change + (scrollPosition % change) : change);
      setScrollPosition(newPosition);
      setRightValue(newPosition > menuRef.current?.scrollWidth - menuRef.current?.clientWidth);
    }
  };

  const handlePrevClick = () => {
    const change = isMobile ? 200 : 225;
    const menu = menuRef.current;
    menu.scrollLeft -=
      scrollPosition % change !== 0 && !isMobile ? change + (scrollPosition % change) : change;
    if (scrollPosition > 0) {
      setRightValue(false);
      const newPosition =
        scrollPosition -
        (scrollPosition % change !== 0 && !isMobile ? change + (scrollPosition % change) : change);
      setScrollPosition(newPosition);
    }
  };

  const generateSponsorItem = (tab) => {
    const dfpSponsorLogos = features.heatmap_sector_ad;
    const logos = dfpSponsorLogos.dfp_sponsor_logos;
    const targetUnit = 'adUnit_' + tab.sectorId;
    if (dfpSponsorLogos.show_from_dfp && has(logos, targetUnit)) {
      return (
        <React.Fragment>
          <span styleName="powered-txt">PARTNERED BY</span>
          <DfpAd
            adUnit={logos[targetUnit].name}
            sizes={[[logos[targetUnit].width, logos[targetUnit].height]]}
          />
        </React.Fragment>
      );
    } else {
      return <span styleName="powered-txt">Sector</span>;
    }
  };

  const takeToStockPage = (symbol, entityId) => {
    try {
      makeApiCall(`/api/v1/entities?type=stock&q=${symbol}&limit=30`).then((res) => {
        if (
          res.entities &&
          res.entities.length > 0 &&
          res.entities.find((ent) => ent['nse-code'] === symbol || ent['stock-id'] === +entityId)
        ) {
          window.open(
            widgetLink(`/stock/${res.entities.find((ent) => ent['nse-code'] === symbol || ent['stock-id'] === +entityId)['stock-id']}/${res.entities.find((ent) => ent['nse-code'] === symbol || ent['stock-id'] === +entityId).slug.replace('-stock', '')}${widgetParam}`, !!widgetParam),
            '_blank',
          );
        }
      });
    } catch (e) {
      console.error('Error in fetching stock entity', e);
    }
  };

  return (
    <>
      <div
        ref={ref}
        styleName={`budget-impacts-ctx`}
        className="section-fluid"
      >
        <div
          styleName={`budget-impacts-inner-ctx ${widget ? 'widget-mode' : ''}`}
          className={'container'}
        >
          {!!widgetParam && (
            <div styleName="headline-ctx">
              <div styleName="rupee-symbol">₹</div>
              <div styleName="headline-texts">
                <span styleName="headline-title">
                  <span styleName="budget">{firstWord}</span> {restText && ` ${restText}`}
                </span>
                <span styleName="sub-headline">{summary}</span>
              </div>
            </div>
          )}
          <div styleName="tabs-ctx">
            <div styleName="tabs-parent" ref={menuRef}>
              {tabs.length > 0 ? (
                <React.Fragment>
                  {tabs.map((tab) => (
                    <div styleName="tab-and-arrow">
                      <div
                        styleName={`tab ${currentTab === tab.key ? 'active' : ''}`}
                        onClick={(event) => selectTab(event, tab)}
                      >
                        <div styleName="tab-and-sponsor">
                          <span styleName="tab-name">{tab.name}</span>
                          <div styleName="sponsor-ctx">{generateSponsorItem(tab)}</div>
                        </div>
                      </div>
                      {currentTab === tab.key && <div styleName="sector-arrow"></div>}
                    </div>
                  ))}
                </React.Fragment>
              ) : (
                <React.Fragment>
                  {Array.apply(null, Array(6)).map((id) => (
                    <div styleName="tab-and-arrow">
                      <div styleName="tab">
                        <div styleName="tab-and-sponsor">
                          <div styleName="loading-name"></div>
                          <div styleName="loading-subname"></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </React.Fragment>
              )}
            </div>
            <div styleName="arrows-parent">
              {!rightValue && <div styleName="arrows-gradient" />}
              <div styleName="nextPrev-wrapper">
                <div
                  styleName={`prev ${scrollPosition <= 0 || initialLoading ? 'disable' : ''}`}
                  onClick={handlePrevClick}
                >
                  <ArrowRightIcon color={'#FFFFFF'} size="16px" />
                </div>
                <div
                  styleName={`next ${rightValue || initialLoading ? 'disable' : ''}`}
                  onClick={handleNextClick}
                >
                  <ArrowRightIcon color={'#FFFFFF'} size="16px" />
                </div>
              </div>
            </div>
          </div>
          {cardsData.length > 0 ? (
            <React.Fragment>
              <div styleName="story-heatmap-ctx">
                {!!tabs.find((tab) => tab.key === currentTab).hero_image && (
                  <BQLink href={tabs.find((tab) => tab.key === currentTab).slug} target="_blank">
                    <div
                      styleName="story"
                      style={{
                        background: `${isMobile ? 'linear-gradient(179deg, #00000000 0%, #262626 100%)' : 'linear-gradient(269deg, #00000000 0%, #262626 50%)'}, url(${tabs.find((tab) => tab.key === currentTab).hero_image}) no-repeat`,
                      }}
                    >
                      <div styleName="story-headline-ctx">
                        <div styleName="current-tab-name">
                          {tabs.find((tab) => tab.key === currentTab).name}
                        </div>
                        <div styleName="headline">
                          {tabs.find((tab) => tab.key === currentTab).headline}
                        </div>
                      </div>
                    </div>
                  </BQLink>
                )}
                <div styleName="cards-ctx">
                  <div styleName="title">
                    Heatmaps for {tabs.find((tab) => tab.key === currentTab).name}
                  </div>
                  <div styleName="cards">{renderCards()}</div>
                </div>
              </div>
              {cardsData.length > (hasStory ? isMobile ? 6 : 10 : isMobile ? 9 : 15) && (
                <React.Fragment>
                  <div styleName="more-ctx">
                    <div styleName="lines" className="desktop-only">
                      <hr styleName="line-1" />
                      <hr styleName="line-2" />
                      <hr styleName="line-3" />
                      <hr styleName="line-4" />
                    </div>
                    <BQLink href={`/markets/equities/budget-impacts${(!!widgetParam ? widgetParam + '&' : '?') +'sector=' + tabs?.find((tab) => tab?.key === currentTab)?.key?.toLowerCase()}`} target="_blank">
                      <div styleName="more-cta">
                        <span styleName="text">View More</span>
                        <span styleName={`arrow-icon`}>
                        <ArrowRightIcon color={'#FFFFFF'} />
                      </span>
                      </div>
                    </BQLink>
                    <div styleName="lines" className="desktop-only">
                      <hr styleName="line-1" />
                      <hr styleName="line-2" />
                      <hr styleName="line-3" />
                      <hr styleName="line-4" />
                    </div>
                  </div>
                </React.Fragment>
              )}
            </React.Fragment>
          ) : (
            <div styleName="story-heatmap-ctx loading">
              <div styleName="story loading">
                <div styleName="first-row"></div>
                <div styleName="second-row"></div>
                <div styleName="third-row"></div>
              </div>
              <div styleName="cards-ctx">
                <div styleName="title">
                  Heatmaps for {tabs?.find((tab) => tab?.key === currentTab)?.name}
                </div>
                <div styleName="cards">
                  {Array.apply(
                    null,
                    Array(5),
                  ).map(() => (
                    <div styleName="card loading">
                      <div styleName="first-row"></div>
                      <div styleName="second-row"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

const mapStateToProps = (state) => ({
  cdnHost: 'media.assettype.com',
  features: state.qt.config.features,
});

export default connect(mapStateToProps)(BudgetImpacts);
